<template>
  <BLayout
    class="container"
    align-center
    @click="handleClick"
  >
    <div class="user">
      <BUserTooltip
        :image-url="(callFeedback.operator || {}).avatarUrl"
        :name="(callFeedback.operator || {}).name"
        :user-id="(callFeedback.operator || {}).id"
        :size="40"
      />
    </div>
    <div class="call-feedback">
      <div class="content">
        <div class="fw-bold">
          {{ i18n.t('training.callFb.title') }}
        </div>
      </div>
      <div class="content-row">
        <div>
          {{ callFeedback.callTarget.companyName }}
        </div>
        <div>
          {{ callFeedback.callTarget.fullName }}
        </div>
      </div>
      <div class="content-row">
        <div class="content-column">
          <span class="label">{{ `${i18n.t('training.callFb.callDateTime')}：` }}</span>{{ callDateTime }}
        </div>
        <div class="content-column">
          <span class="label">{{ `${i18n.t('training.callFb.connectedTo')}：` }}</span>{{ connectedToText(callFeedback.connectedTo) }}
        </div>
        <div class="content-column">
          <span class="label">{{ `${i18n.t('training.callFb.callResultType')}：` }}</span>{{ callFeedback.callResultTypeText }}
        </div>
      </div>
    </div>
  </BLayout>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { CallFeedback } from '@/api/openapi';
import { useRouterLink } from '@/composable/router-link';
import { startOf, add, formatDateTimeRange, formatISO8601Date } from '@/utils/date-time';

type TProps = {
  callFeedback: CallFeedback;
};

const props = defineProps<TProps>();

const i18n = useI18n();
const { linkTo } = useRouterLink();

const handleClick = () => {
  linkTo({
    name: 'Training',
    query: {
      callResultId: props.callFeedback.callResultId,
      startDate: startDate.value,
      endDate: endDate.value,
    },
  });
};

const callDateTime = computed<string>(() => {
  return formatDateTimeRange(props.callFeedback.startedAt, props.callFeedback.endedAt);
});
const startDate = computed(() => {
  return formatISO8601Date(startOf(props.callFeedback.startedAt, 'day'));
});
const endDate = computed(() => {
  return formatISO8601Date(add(startOf(props.callFeedback.startedAt, 'day'), 14, 'day'));
});

const connectedToText = (connectedTo: string) => {
  if (connectedTo === 'unknown') {
    return i18n.t('callResult.connectedTo.enum.unknown');
  } else if (connectedTo === 'not_stakeholder') {
    return i18n.t('callResult.connectedTo.enum.notStakeholder');
  } else if (connectedTo === 'stakeholder') {
    return i18n.t('callResult.connectedTo.enum.stakeholder');
  }
  return '';
};
</script>

<style lang="scss" scoped>
.container {
  padding: $basespace-100 $basespace-300;
  border-bottom: 1px solid $bdcolor-light;
  &:last-child {
    border-bottom: none;
  }

  &:hover {
    display: flex;
    justify-content: right;
    background-color: $bgcolor-dark;
    cursor: pointer;
  }

  .user {
    padding-right: $basespace-300;
  }

  .call-feedback {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: $basespace-100 $basespace-300;
    overflow: hidden;
    width: 100%;

    .content-row {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: $basespace-100;
      overflow: hidden;

      .content-column {
        overflow: hidden;
        height: 20px;
      }
      .label {
        color: $textcolor-light;
        padding-bottom: $basespace-50;
      }
    }
  }
}
</style>
